import React from 'react';
import { motion } from 'framer-motion';
import { 
  Sun, 
  ShieldCheck, 
  Camera, 
  Heart, 
  Map, 
  Briefcase, 
  CheckCircle2, 
  ArrowRight,
  Info
} from 'lucide-react';
import ScrollToTop from '../components/Common/ScrollToTop';

const TravelTips: React.FC = () => {
  const tips = [
    {
      category: 'Packing Essentials',
      description: 'Prepare for the diverse landscapes and shifting temperatures of East Africa.',
      icon: <Briefcase className="w-6 h-6" />,
      items: [
        {
          title: 'Strategic Clothing',
          description: 'Pack neutral-colored clothing (beige, khaki, olive) to blend with the environment and avoid attracting Tsetse flies.',
          details: [
            'Lightweight, breathable fabrics for warm days',
            'Warm technical layers for cool dawn game drives',
            'Full-coverage attire for sun & insect protection',
            'Durable, comfortable walking boots or sandals'
          ]
        },
        {
          title: 'Photography Gear',
          description: 'Capture the majesty of the Great Migration and the Big Five.',
          details: [
            'DSLR/Mirrorless with telephoto lens (min 300mm)',
            'High-speed memory cards & spare batteries',
            'Professional-grade binoculars (8x42 recommended)',
            'Sensor cleaning kit for savannah dust'
          ]
        },
        {
          title: 'Health & Essentials',
          description: 'Maintain peak wellness throughout your safari adventure.',
          details: [
            'Prescription meds & custom first aid kit',
            'Eco-friendly SPF 50+ sunscreen & high-DEET repellent',
            'Water purification tablets (just in case)',
            'Personal hygiene wipes & biodegradable soaps'
          ]
        }
      ]
    },
    {
      category: 'The Safari Experience',
      description: 'Maximizing your wildlife encounters across the Serengeti and Masai Mara.',
      icon: <Camera className="w-6 h-6" />,
      items: [
        {
          title: 'Optimal Viewing',
          description: 'Timing is everything when tracking predators and migration flows.',
          details: [
            'Dawn drives (06:00) for high predator activity',
            'Golden hour (16:30) for dramatic photography',
            'Patience is key - wait for the story to unfold',
            'Trust your guide\'s expert intuition'
          ]
        },
        {
          title: 'Safari Etiquette',
          description: 'Respecting the natural rhythm of the wild.',
          details: [
            'Maintain silence during animal encounters',
            'Never stand up or move suddenly in the vehicle',
            'Stay on designated tracks to protect the tundra',
            'Always stay inside the vehicle unless at safe zones'
          ]
        },
        {
          title: 'Understanding the Wild',
          description: 'Deepen your connection with the flora and fauna.',
          details: [
            'Observe animal body language and signals',
            'Ask questions about ecological relationships',
            'Focus on the small wonders, not just the Big Five',
            'Journal your daily sightings and reflections'
          ]
        }
      ]
    }
  ];

  const seasonalOverview = [
    {
      season: 'Dry Season (June - October)',
      status: 'Peak Safari Visibility',
      desc: 'Animals congregate around permanent water sources, making viewing highly predictable.',
      features: ['Great Migration crossing Masai Mara/Serengeti', 'Clear skies & sparse vegetation', 'Cool, pleasant temperature range'],
      tip: 'Book 6-12 months ahead for iconic river crossings.'
    },
    {
      season: 'Green Season (November - May)',
      status: 'Lush Landscapes & Calving',
      desc: 'Dust-free air and vibrant greenery create spectacular conditions for photography.',
      features: ['Magnificent bird migration waves', 'Newborn animals & predator action', 'Intimate encounters with fewer travelers'],
      tip: 'Perfect for birders and photographers seeking dramatic light.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  } as const;

  return (
    <div className="min-h-screen bg-[#F8FBFF] font-sans selection:bg-primary-100">
      {/* Hero Section */}
      <div className="relative pt-32 pb-24 px-6 overflow-hidden bg-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 border border-primary-100 mb-6 font-bold text-primary-600">
            <Map className="w-4 h-4 mr-2" />
            <span className="text-[10px] uppercase tracking-widest luxury-font">Preparation Guide</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-primary-900 tracking-tight font-serif mb-6">
            Expert <span className="text-primary-600">Travel Tips</span>
          </h1>
          <p className="text-lg text-neutral-500 font-medium max-w-2xl mx-auto">
            Professional advice tailored for the ultimate East African safari expedition.
          </p>
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-32">
        {/* Core Categories */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-24"
        >
          {tips.map((category, idx) => (
            <motion.div key={idx} variants={itemVariants} className="space-y-12">
              <div className="max-w-2xl">
                <div className="flex items-center space-x-3 text-primary-600 mb-2">
                  {category.icon}
                  <span className="text-xs font-bold uppercase tracking-widest luxury-font">Category 0{idx + 1}</span>
                </div>
                <h2 className="text-3xl font-extrabold text-primary-900 font-serif mb-4">{category.category}</h2>
                <p className="text-neutral-500 font-medium mb-8 leading-relaxed">{category.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {category.items.map((item, i) => (
                  <div key={i} className="group p-8 rounded-[32px] bg-white border border-blue-50 shadow-[0_20px_40px_-15px_rgba(42,126,206,0.05)] hover:shadow-[0_40px_80px_-20px_rgba(42,126,206,0.1)] transition-all duration-500">
                    <h3 className="text-xl font-bold text-primary-900 mb-4 group-hover:text-primary-600 transition-colors">{item.title}</h3>
                    <p className="text-sm text-neutral-400 font-medium mb-6 leading-relaxed">{item.description}</p>
                    <ul className="space-y-3">
                      {item.details.map((detail, dIdx) => (
                        <li key={dIdx} className="flex items-start text-xs text-neutral-500 font-bold leading-snug">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Seasonal Guide */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 p-12 rounded-[48px] bg-white border border-blue-50 shadow-[0_40px_80px_-20px_rgba(42,126,206,0.08)]"
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-primary-900 font-serif mb-4">Seasonal Intelligence</h2>
            <p className="text-neutral-500 font-medium">East Africa's rhythms change with the rains. Choosing your window is the first step.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {seasonalOverview.map((season, i) => (
              <div key={i} className="relative p-8 rounded-[32px] bg-neutral-50/50 border border-transparent hover:border-primary-100 transition-colors">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-primary-900 uppercase tracking-tight">{season.season}</h3>
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-[10px] font-bold rounded-full uppercase tracking-widest">{season.status}</span>
                </div>
                <p className="text-neutral-500 font-medium mb-8 text-sm leading-relaxed">{season.desc}</p>
                <div className="space-y-3 mb-8">
                  {season.features.map((f, fi) => (
                    <div key={fi} className="flex items-center text-xs font-bold text-primary-800">
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3" />
                      {f}
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-white rounded-2xl border border-blue-50 flex items-start space-x-3">
                  <Info className="w-4 h-4 text-primary-600 mt-0.5" />
                  <p className="text-[11px] text-neutral-400 font-bold"><span className="text-primary-900">PRO TIP:</span> {season.tip}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Global CTA */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-32 relative rounded-[40px] overflow-hidden group shadow-2xl"
        >
          <img 
            src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?q=80&w=2671&auto=format&fit=crop" 
            alt="Prepare for safari" 
            className="w-full h-[400px] object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-primary-900/40 backdrop-blur-[2px] flex items-center justify-center text-center px-6">
            <div className="max-w-2xl text-white">
              <h2 className="text-3xl md:text-5xl font-extrabold font-serif mb-8 leading-tight">Master the art of the African expedition.</h2>
              <button className="inline-flex items-center px-10 py-5 bg-blue-500 text-white rounded-full font-bold uppercase tracking-widest text-xs hover:bg-blue-600 transition-all shadow-2xl shadow-blue-500/30 group/btn">
                Download Full Guide
                <ArrowRight className="w-4 h-4 ml-4 group-hover/btn:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
      
      <ScrollToTop />
    </div>
  );
};

export default TravelTips;
